import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { firstValueFrom } from 'rxjs';
import { CustomToastService } from 'src/app/core/services/custom-toast.service';
import { MasterService } from 'src/app/core/services/master.service';
import { environment } from 'src/app/environments/environment';
import { StaticLookup } from 'src/app/features/company-portal/modules/suppliers/models/SupplierModel';
import { UserModel } from 'src/app/features/company-portal/modules/user-management/models/user-management.models';
import { UsersManagementService } from 'src/app/features/company-portal/modules/user-management/services/user-management.service';
import { onUserLogin } from 'src/main';
import { ROOT_COMPANY_ID } from '../../constants/constants';
import { Apps, TPMUserModules } from '../../enums/enums';
import { LoginModel, RumSettings } from '../../models/shared.models';
import { SharedService } from './../../services/shared.service';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css'],
  standalone: false,
})
export class LoginComponent {
  toggleForgotPassField: any;
  userInfo: any;
  showPassword: boolean = false;
  loginInfo: LoginModel = new LoginModel();
  companyId: number = 0;
  redirectTo: string = '';
  incorrectLogin: boolean = false;
  isLoggingIn: boolean = false;
  dataDagConfig: RumSettings = new RumSettings();
  tpmUrl: string = environment.tpmUrl;

  constructor(
    private sharedService: SharedService,
    private router: Router,
    private route: ActivatedRoute,
    private master: MasterService,
    private toast: CustomToastService,
    private userService: UsersManagementService,
    private cookieService: CookieService,
  ) {}

  ngOnInit(): void {
    this.redirectTo = this.route.snapshot.queryParams['returnUrl'] || '';
  }

  toggleForm() {
    this.toggleForgotPassField = !this.toggleForgotPassField;
  }

  togglePasswordVisibility1() {
    this.showPassword = !this.showPassword;
  }

  // Method for sending an emial to the user for forget password functionality
  sendEmailForForgetPass() {
    this.sharedService.resetPasswordInitiate(this.loginInfo).subscribe(
      (res) => {
        this.toast.success('Email has been sent successfully to the entered email address.');
        this.loginInfo = new LoginModel();
      },
      (erro) => {
        this.toast.error('Inavalid Email');
        this.loginInfo = new LoginModel();
      },
    );
  }

  // Methods for the user login
  removeCustomPrefix(tokenData: Record<string, any>): Record<string, any> {
    const cleanedData: Record<string, any> = {};

    Object.keys(tokenData).forEach((key) => {
      const newKey = key.startsWith('custom:') ? key.replace('custom:', '') : key;
      cleanedData[newKey] = tokenData[key];
    });

    return cleanedData;
  }

  loginOnEnter(event: Event) {
    event.preventDefault();
    if (this.isLoggingIn) return; // prevent multiple enter key presses
    if (this.loginInfo.email && this.loginInfo.password) {
      this.login();
    }
  }

  async login() {
    if (this.isLoggingIn) return; // Prevent multiple concurrent login attempts
    this.isLoggingIn = true;
    this.incorrectLogin = false;
    const loginPayload = this.loginInfo.getLoginPayload();
    try {
      const response: any = await firstValueFrom(this.sharedService.login(loginPayload));
      if (response && response.accessToken) {
        await this.loadLookups();
        this.userInfo = this.master.decodeJwt(response.accessToken);
        const cleanedData: any = this.removeCustomPrefix(this.userInfo);
        localStorage.setItem('user', JSON.stringify(cleanedData));
        sessionStorage.setItem('sessionId', response.sessionId);
        this.cookieService.set('sessionId', response.sessionId);
        if (
          window.location.hostname === 'localhost' ||
          window.location.hostname === '127.0.0.1'
        ) {
          this.cookieService.delete('logoutEvent', '/');
          this.cookieService.set('accessToken', response.accessToken, undefined, '/');
          this.cookieService.set('refreshToken', response.refreshToken, undefined, '/');
        } else {
          this.cookieService.delete('logoutEvent', '/', `.${environment.domainName}`);
          this.cookieService.set(
            'accessToken',
            response.accessToken,
            undefined,
            '/',
            `.${environment.domainName}`,
          );
          this.cookieService.set(
            'refreshToken',
            response.refreshToken,
            undefined,
            '/',
            `.${environment.domainName}`,
          );
        }

        if (this.redirectTo && this.redirectTo.trim() !== '') {
          if (this.redirectTo.startsWith('http')) {
            window.location.href = this.redirectTo;
          } else {
            this.router.navigateByUrl(this.redirectTo);
          }
        } else if (Number(cleanedData.companyid) == ROOT_COMPANY_ID) {
          this.router.navigate(['/admin-portal/admin-dashboard']);
        } else {
          this.loadRumConfig();
        }
      } else {
        this.incorrectLogin = true;
      }
    } catch (error) {
      this.incorrectLogin = true;
      this.loginInfo.password = '';
    } finally {
      this.isLoggingIn = false; // Reset flag after request completes
    }
  }

  async loadLookups() {
    const lookups: StaticLookup[] = await firstValueFrom(this.sharedService.setLookups());
    localStorage.setItem('enums', JSON.stringify(lookups));
  }

  async loadRumConfig(isAfterUpdateCall: boolean = true) {
    this.dataDagConfig = await firstValueFrom(
      this.sharedService.getDatadogConfig(ROOT_COMPANY_ID, isAfterUpdateCall),
    );
    await this.loadUser(true, this.dataDagConfig);
  }

  async loadUser(isAfterUpdateCall: boolean = false, dataDogConfig: RumSettings) {
    const user = this.master.getUserDetailFromStorage();
    if (!user) return;
    try {
      const res = await firstValueFrom(
        this.userService.getUserLiteById(user.userid, isAfterUpdateCall),
      );

      const fetchedUser = res as UserModel;

      if (dataDogConfig.enableRumForCompanyApp) {
        const loggedInUser = {
          id: fetchedUser.id.toString(),
          name: fetchedUser.username,
          email: fetchedUser.email,
          companyId: fetchedUser.companyId,
        };

        onUserLogin(loggedInUser);
      }

      const userApps = fetchedUser.apps.filter((app) => app.id != Apps.CompanyPortal);

      /* ---------- ONLY TPM ---------- */
      if (
        userApps.length === 1 &&
        userApps[0].id === Apps.TPM &&
        (fetchedUser.defaultModuleId === TPMUserModules.Production ||
          !fetchedUser.hasAccessToAdminModule)
      ) {
        this.redirectToModule(Apps.TPM);
        return;
      }

      /* ---------- ONLY IMS (NEW) ---------- */
      if (userApps.length === 1 && userApps[0].id === Apps.Inventory) {
        this.redirectToModule(Apps.Inventory);
        return;
      }

      /* ---------- MULTIPLE APPS ---------- */
      this.router.navigate(['']);
    } catch (error) {
      console.error('Error loading user:', error);
    }
  }

  isLoginValid() {
    return this.loginInfo.email.trim() !== '' && this.loginInfo.password.trim() !== '';
  }
  isResetPasswordEmailValid() {
    return this.loginInfo.userName.trim() !== '';
  }

  redirectToModule(appId: number) {
    switch (appId) {
      case Apps.TPM:
        const tpmBaseUrl = this.tpmUrl.split('?')[0];
        window.location.href = `${tpmBaseUrl}?appId=${appId}`;
        break;

      case Apps.Inventory:
        const imsBaseUrl = environment.imsUrl.split('?')[0];
        window.location.href = `${imsBaseUrl}`;
        break;

      default:
        console.error('Invalid module name');
        return;
    }
  }
}
